import Avatar from '@mui/material/Avatar';
import type { Currency } from '@ps/web-core-enums';
import { Toggle, Translation } from '@ps/web-shared-components';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useField } from 'react-final-form';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import flags from '@swf/core/assets/flags';

import type { PendingCashoutOption } from '../../../../../../types/Cashier';
import PaymentMethodLogo from '../../../../../PaymentMethodLogo/PaymentMethodLogo';

import { TXTWCL_Total_you_receive } from './PendingCashouts.messages.json';
import {
  amount,
  avatar,
  currency,
  instrumentNumber,
  singleTransaction,
  toggle,
  totalSummary
} from './PendingCashouts.scss';

interface TransactionProps {
  selectedCurrency?: string;
  fieldName: string;
  transaction: PendingCashoutOption;
  useToggle?: boolean;
}

interface PendingCashoutsProps {
  fieldName: string;
  pendingCashouts: PendingCashoutOption[];
}

const Transaction: React.FC<TransactionProps> = ({
  selectedCurrency,
  fieldName,
  transaction,
  useToggle
}) => {
  const isCheckboxDisabled =
    transaction.balanceCurrency !== selectedCurrency && !isEmpty(selectedCurrency);

  return (
    <div className={singleTransaction} key={transaction.transactionId}>
      <PaymentMethodLogo cardType={transaction.type} variant="result" />
      <div>
        <div className={currency}>
          <CurrencyAmount
            currency={transaction.balanceCurrency}
            value={transaction.balanceAmount}
            currencyDisplay="code"
          />
        </div>
        <span className={instrumentNumber}>{transaction.accountNumber}</span>
      </div>

      {useToggle && (
        <Toggle
          id={`transaction-${transaction.transactionId}`}
          name={fieldName}
          label={transaction.transactionId}
          hideLabel
          type="checkbox"
          value={transaction.transactionId}
          className={toggle}
          disabled={isCheckboxDisabled}
        />
      )}
    </div>
  );
};

const PendingCashouts: React.FC<PendingCashoutsProps> = ({ fieldName, pendingCashouts }) => {
  const {
    input: { value: selectedTransactions, onChange }
  } = useField(fieldName);

  const [selectedCurrency, setSelectedCurrency] = useState('');

  const checkSelectedCheckboxTitle = useCallback(() => {
    const selectedCurrencyTransaction = pendingCashouts.find(
      ({ transactionId }) => transactionId === selectedTransactions[0]
    );

    setSelectedCurrency(selectedCurrencyTransaction?.balanceCurrency || '');
  }, [selectedTransactions, pendingCashouts]);

  useEffect(() => {
    if (pendingCashouts.length === 1 && isEmpty(selectedTransactions)) {
      onChange([pendingCashouts[0].transactionId]);
    }
    checkSelectedCheckboxTitle();
  }, [onChange, pendingCashouts, selectedTransactions, checkSelectedCheckboxTitle]);

  const isSingleTransaction = pendingCashouts.length === 1;

  const totalAmountObj = ((selectedTransactions as string[]) || []).reduce(
    (result, current) => {
      const transaction = pendingCashouts.find(
        (cashout) => cashout.transactionId === current
      ) as PendingCashoutOption;

      if (!transaction) {
        return result;
      }

      if (result[transaction.balanceCurrency]) {
        result[transaction.balanceCurrency] =
          result[transaction.balanceCurrency] + transaction.balanceAmount;
      } else {
        result[transaction.balanceCurrency] = transaction.balanceAmount;
      }
      return result;
    },
    {} as Record<string, number>
  );

  return (
    <>
      {isSingleTransaction ? (
        <Transaction fieldName={fieldName} transaction={pendingCashouts[0]} />
      ) : (
        pendingCashouts.map((transaction) => (
          <Transaction
            key={transaction.transactionId}
            fieldName={fieldName}
            transaction={transaction}
            selectedCurrency={selectedCurrency}
            useToggle
          />
        ))
      )}
      {!isEmpty(selectedTransactions) && (
        <div className={totalSummary}>
          <div>
            <Translation {...TXTWCL_Total_you_receive} />
          </div>
          <div>
            {Object.keys(totalAmountObj).map((current) => (
              <div key={current} className={amount}>
                +{' '}
                <CurrencyAmount
                  currency={current as Currency}
                  value={totalAmountObj[current]}
                  currencyDisplay="code"
                />
                <Avatar classes={{ root: avatar }} src={flags[current.substring(0, 2)]} />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default PendingCashouts;
